import React from "react";
import { Target, HeartHandshake, Compass } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PrimaryButton } from "../components/Buttons";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Velacin",
  url: "https://velacin.com",
  logo: "https://velacin.com/logo.png",
  sameAs: [
    "https://www.linkedin.com/company/velacin",
    "https://twitter.com/velacin",
    "https://github.com/velacin",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    email: "hello@velacin.com",
    contactType: "sales",
    areaServed: "Worldwide",
  },
};

const STORY = [
  "Every mid-sized team we've worked with has the same problem: an HRIS that doesn't know what people are working on, a PM tool that doesn't know who's on leave, a timesheet that doesn't know which project it's billing, and a spreadsheet holding the whole thing together.",
  "We built Velacin because we were tired of solving the same integration problem in every job. What if the tools weren't separate at all? What if HR, PM, timesheets, expenses, recruitment, and analytics shared the same data model, the same permissions, and the same design language from day one?",
  "That's Velacin. Twenty modules that don't just integrate — they cohere. Approved timesheets automatically become project labor costs. Attendance feeds insights. Achievements come from your actual work. Custom roles you invent apply everywhere.",
  "We're a remote-first team building for other remote-first teams. We ship often, we listen loudly, and we'd rather build the right feature slowly than the wrong feature fast.",
];

const PRINCIPLES = [
  {
    icon: Target,
    title: "Focused",
    body: "We build modules that do one thing exceptionally, then let them compose. No feature bloat, no half-finished experiments.",
  },
  {
    icon: HeartHandshake,
    title: "Humane",
    body: "Software about people should treat them like people. Transparent tracking, opt-in surveillance, and defaults biased toward autonomy.",
  },
  {
    icon: Compass,
    title: "Opinionated",
    body: "Configuration is a burden. Velacin ships with defaults that are correct for 90% of teams, and controls for the other 10.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Velacin"
        description="Velacin is a modern workspace for teams that ship. We combine HR and project management into a coherent product built on humane defaults and opinionated design."
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="bg-black pt-36 pb-16 md:pt-44 md:pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <Eyebrow>About</Eyebrow>
            <h1 className="mt-4 max-w-4xl text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
              One product,
              <br />
              <span className="font-light italic text-white/70">
                built on the belief that tools should disappear.
              </span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-black pb-24 md:pb-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="space-y-6">
            {STORY.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className="text-lg leading-relaxed text-white/70">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Principles</Eyebrow>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              How we build.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <article
                  data-testid={`principle-${i}`}
                  className="card-surface h-full rounded-3xl p-8"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#4C6EF5]/15 text-[#8FA4FF]">
                    <p.icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    {p.body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-black py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Let's talk.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base text-white/65 md:text-lg">
              We love hearing from teams building things worth building.
            </p>
            <div className="mt-9 flex justify-center">
              <PrimaryButton to="/contact" testId="about-get-in-touch">
                Get in touch
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
